import { AntInput } from "@/CommonElements/AntInput";
import { AntSelect } from "@/CommonElements/AntSelect";
import { Controllers } from "@/CommonElements/Controller";
import { PageHeader } from "@/CommonElements/PageTitle";
import TableC from "@/CommonElements/TableC";
import { ActionsFooter } from "@/Component/ActionsFooter";
import { cn, filterObject } from "@/utils/helpers";
import { useEffect, useState } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { Button } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Container, Spinner } from "reactstrap";
import {
  useHousingContractDetailReport,
  useHousingContractDetailReportMutation,
} from "@/services/reports";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";

export const HousingContractDetailReport = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const { getLocalizedText } = useLocalizedText();

  const {
    mutate: generateHousingContractDetailReport,
    isPending: isGeneratingHousingContractDetailReport,
  } = useHousingContractDetailReportMutation();

  const form = useForm({
    defaultValues: {
      format: "pdf",
      orientation: "portrait",
    },
    disabled: isGeneratingHousingContractDetailReport,
  });

  const format = form.watch("format");
  const isJson = format === "json";

  const filters = filterObject({
    ...form.getValues(),
    format: "json",
    page,
  });

  const {
    data: housingContractDetailRes,
    isLoading: isLoadingHousingContractDetail,
    refetch,
    isRefetching: isRefetchingHousingContractDetail,
  } = useHousingContractDetailReport({
    params: filters,
  });

  const housingContractDetails = housingContractDetailRes?.data?.data || [];
  const meta = housingContractDetailRes?.data?.meta || {};

  const {
    control,
    formState: { errors },
    getValues,
  } = form;

  const formText = {
    json: {
      buttonText: t("VIEW_REPORTS"),
    },
    pdf: {
      buttonText: t("EXPORT_PDF"),
    },
    excel: {
      buttonText: t("EXPORT_EXCEL"),
    },
  };

  const formContent = formText[format] || formText.json;

  useEffect(() => {
    if (isJson && page > 1) {
      refetch();
    }

  }, [page, isJson, refetch]);
  const ctrls = [
    {
      labelProps: { children: t("REPORT_FORMAT") },
      controllerProps: {
        name: "format",
        render: ({ field, fieldState: { error } }) => (
          <AntSelect
            {...field}
            allowClear
            id="format"
            placeholder={t("REPORT_FORMAT")}
            status={error ? "error" : undefined}
            options={[
              { value: "json", label: t("VIEW_REPORTS") },
              { value: "pdf", label: t("EXPORT_PDF") },
              { value: "excel", label: t("EXPORT_EXCEL") },
            ]}
          />
        ),
      },
    },
    !isJson && {
      labelProps: { children: t("ORIENTATION") },
      controllerProps: {
        name: "orientation",
        render: ({ field, fieldState: { error } }) => (
          <AntSelect
            {...field}
            allowClear
            id="orientation"
            placeholder={t("ORIENTATION")}
            status={error ? "error" : undefined}
            options={[
              { value: "portrait", label: t("PORTRAIT") },
              { value: "landscape", label: t("LANDSCAPE") },
            ]}
          />
        ),
      },
    },
    {
      labelProps: { children: t("CONTRACT_ID") },
      controllerProps: {
        name: "contract_id",
        render: ({ field, fieldState: { error } }) => (
          <AntInput
            {...field}
            allowClear
            id="contract_id"
            placeholder={t("CONTRACT_ID")}
            status={error ? "error" : undefined}
          />
        ),
      },
    },
    {
      labelProps: { children: t("REQUEST_ID") },
      controllerProps: {
        name: "request_id",
        render: ({ field, fieldState: { error } }) => (
          <AntInput
            {...field}
            allowClear
            id="request_id"
            placeholder={t("REQUEST_ID")}
            status={error ? "error" : undefined}
          />
        ),
      },
    },
    {
      labelProps: { children: t("CONTRACT_START_FROM") },
      controllerProps: {
        name: "contract_start_from",
        render: ({ field, fieldState: { error } }) => (
          <DatePicker
            style={{
              height: "40px",
            }}
            allowClear
            id="contract_start_from"
            className="w-100"
            placeholder={t("CONTRACT_START_FROM")}
            status={error ? "error" : undefined}
            format="YYYY-MM-DD"
            value={field.value ? dayjs(field.value) : null}
            onChange={(date) =>
              field.onChange(date ? date.format("YYYY-MM-DD") : null)
            }
          />
        ),
      },
    },
    {
      labelProps: { children: t("CONTRACT_START_TO") },
      controllerProps: {
        name: "contract_start_to",
        render: ({ field, fieldState: { error } }) => (
          <DatePicker
            style={{
              height: "40px",
            }}
            allowClear
            id="contract_start_to"
            className="w-100"
            placeholder={t("CONTRACT_START_TO")}
            status={error ? "error" : undefined}
            format="YYYY-MM-DD"
            value={field.value ? dayjs(field.value) : null}
            onChange={(date) =>
              field.onChange(date ? date.format("YYYY-MM-DD") : null)
            }
          />
        ),
      },
    },
    {
      labelProps: { children: t("CONTRACT_END_FROM") },
      controllerProps: {
        name: "contract_end_from",
        render: ({ field, fieldState: { error } }) => (
          <DatePicker
            style={{
              height: "40px",
            }}
            allowClear
            id="contract_end_from"
            className="w-100"
            placeholder={t("CONTRACT_END_FROM")}
            status={error ? "error" : undefined}
            format="YYYY-MM-DD"
            value={field.value ? dayjs(field.value) : null}
            onChange={(date) =>
              field.onChange(date ? date.format("YYYY-MM-DD") : null)
            }
          />
        ),
      },
    },
    {
      labelProps: { children: t("CONTRACT_END_TO") },
      controllerProps: {
        name: "contract_end_to",
        render: ({ field, fieldState: { error } }) => (
          <DatePicker
            style={{
              height: "40px",
            }}
            allowClear
            id="contract_end_to"
            className="w-100"
            placeholder={t("CONTRACT_END_TO")}
            status={error ? "error" : undefined}
            format="YYYY-MM-DD"
            value={field.value ? dayjs(field.value) : null}
            onChange={(date) =>
              field.onChange(date ? date.format("YYYY-MM-DD") : null)
            }
          />
        ),
      },
    },
    {
      labelProps: { children: t("ORGANIZER") },
      controllerProps: {
        name: "mission_id",
        render: ({ field, fieldState: { error } }) => (
          <SelectCachedOptions
            {...field}
            allowClear
            showSearch
            id="mission_id"
            placeholder={t("ORGANIZER")}
            status={error ? "error" : undefined}
            optionKey="missions"
            options={(m) => ({
              value: m.id,
              label: getLocalizedText(m.name_ar, m.name_en),
            })}
          />
        ),
      },
    },
    {
      labelProps: { children: t("AFFILIATED_ORGANIZER") },
      controllerProps: {
        name: "company_id",
        render: ({ field, fieldState: { error } }) => (
          <SelectCachedOptions
            {...field}
            allowClear
            showSearch
            id="mission_id"
            placeholder={t("AFFILIATED_ORGANIZER")}
            status={error ? "error" : undefined}
            optionKey="companies"
            options={(c) => ({
              value: c.id,
              label: getLocalizedText(c.name_ar, c.name_en),
            })}
          />
        ),
      },
    },
    {
      labelProps: { children: t("SERVICE_CENTER") },
      controllerProps: {
        name: "service_center_id",
        render: ({ field, fieldState: { error } }) => (<SelectCachedOptions
          {...field}
          allowClear
          id={field.name}
          placeholder={t("SERVICE_CENTER")}
          status={error ? "error" : undefined}
          optionKey="service_centers"
          options={(sc) => ({
            value: sc.id,
            label: getLocalizedText(sc.name_ar, sc.name_en),
          })}
        />
        ),
      },
    },
  ].filter(Boolean);

  const columns = [
    {
      name: t("CONTRACT_ID"),
      key: "contract_id",
    },
    {
      name: t("PERMIT_NO"),
      key: "permit_no",
    },
    {
      name: t("CONTRACT_START_DATE"),
      key: "contract_start",
    },
    {
      name: t("CONTRACT_END_DATE"),
      key: "contract_end",
    },
    {
      name: t("PERMIT_NO"),
      key: "permit_no",
    },
    {
      name: t("CLIENT"),
      key: "client",
    },
    {
      name: t("CITY"),
      key: "city_name",
    },
  ];

  const onSubmit = (data) => {
    const payload = {
      ...getValues(),
      page,
    };

    if (data.format === "json") {
      setPage(1);
      return refetch();
    }

    generateHousingContractDetailReport({ params: payload });
  };

  const backTo =
    window.history.length > 1 ? -1 : `/app/makkah/housing-reports`;

  const isLoading =
    isGeneratingHousingContractDetailReport || isLoadingHousingContractDetail;

  return (
    <div>
      <PageHeader title={t("HOUSING_CONTRACT_DETAIL_REPORT_TITLE")} sticky={false} />
      <Container fluid>
        <form onSubmit={form.handleSubmit(onSubmit)} className="mb-4">
          <Controllers
            watch={form.watch}
            controllers={ctrls.map((config) => ({
              ...config,
              labelProps: {
                ...config.labelProps,
                className: cn("fw-bold", config.labelProps.className),
              },
              controllerProps: {
                ...config.controllerProps,
                control,
                errors,
              },
              layout: {
                colProps: { xs: 6, md: 6 },
              },
            }))}
          />
          <ActionsFooter>
            <Button disabled={isLoading} className="btn btn-primary btn-sm" type="submit">
              {isLoading ? <Spinner size="sm" /> : formContent.buttonText}
            </Button>
            <Link
              to={backTo}
              disabled={isLoading}
              style={{ pointerEvents: isLoading ? "none" : "auto" }}
              className="btn btn-danger btn-sm"
              type="button"
            >
              {t("CANCEL")}
            </Link>
            <button
              onClick={() => form.reset()}
              disabled={isLoading} className="btn btn-outline-primary btn-sm" type="button">
              {t("RESET")}
            </button>
          </ActionsFooter>
        </form>

        {isJson && (
          <TableC
            columnsFilter={false}
            data={housingContractDetails}
            columns={columns}
            loading={
              isLoadingHousingContractDetail ||
              isRefetchingHousingContractDetail
            }
            skeletonCount={10}
            pagination={{
              current: page,
              total: meta?.total || 0,
              pageSize: meta?.per_page || 10,
              onChange: (newPage) => {
                setPage(newPage);
              },
            }}
          />
        )}
      </Container>
    </div>
  );
};

